/**
 * Static blog seed. Keeps the stack simple (no DB round-trip for reads).
 * Each post is ~1200+ words, 2-3 internal links, FAQ at the bottom.
 */
export const BLOG_POSTS = [
  {
    slug: "signs-elderly-parent-needs-in-home-care",
    title: "10 Signs an Elderly Parent in SWFL Needs In-Home Care",
    excerpt:
      "The quiet, early signals most adult children miss — and how to have the conversation without hurting a parent's pride.",
    date: "2026-02-14",
    readMinutes: 7,
    heroImage:
      "https://images.unsplash.com/photo-1559757148-5c350d0d3c56?auto=format&fit=crop&w=1600&q=80",
    heroAlt:
      "Adult daughter sitting with elderly mother in a Naples living room, having a quiet conversation",
    keywords: "signs elderly parent needs in-home care, Naples senior care",
    sections: [
      {
        h2: "Why these signals matter",
        body:
          "In Southwest Florida, families often live far apart. A parent in Naples. A son in Boston. By the time the phone call comes — usually after a fall, a hospital stay, a grocery delivery no one received — the easy, gentle care options have narrowed. These ten signals are the ones trained caregivers watch for. Noticing them a few months early changes everything.",
      },
      {
        h2: "1. Unopened mail and unpaid bills",
        body:
          "Executive function is the first thing to quietly slip. If a dining-room table that used to be spotless now has a growing pile of unopened envelopes, that is almost always a signal — not laziness, not disinterest. Our caregivers often start with simple mail-sorting as part of light housekeeping, and the household calms down almost immediately.",
      },
      {
        h2: "2. Weight loss or an emptier fridge",
        body:
          "Cooking for one is hard. Cooking for one at 82, with arthritic hands and bad lighting over the stove, is harder. If a parent's clothes are suddenly looser or the fridge has fewer leftovers than it used to, meal prep is the single highest-ROI service to start with.",
      },
      {
        h2: "3. Medication confusion",
        body:
          "Even smart, capable adults can lose track of 7 to 11 daily medications. If pill bottles look full long after their refill date, if your parent asks the same question about a prescription twice in a visit, or if a hospital stay has introduced new drugs, medication reminder support is urgent.",
      },
      {
        h2: "4. A fall — even a small one",
        body:
          "A single fall doubles the probability of a second fall within six months. If your parent mentions it casually, or you see a new bruise they can't quite explain, that is the moment to put professional eyes on the home. Fall-prevention audits are a core part of our personal care intake.",
      },
      {
        h2: "5. Driving close calls",
        body:
          "Scrapes on the garage column. A fender dent that \"was already there.\" A reluctance to drive at night. Transportation assistance replaces the keys with dignity — a familiar face driving them to the hairdresser, the cardiologist, the grandkids.",
      },
      {
        h2: "6. Social withdrawal",
        body:
          "Isolation ages the brain faster than almost any disease. If your parent's phone no longer rings, if church attendance has dropped off, if the standing bridge game died quietly — a companion caregiver fills that space with real human warmth. [LINK:/services/companion-care]",
      },
      {
        h2: "7. Repeating questions in the same conversation",
        body:
          "Short-term memory lapses are not always Alzheimer's — but they are always worth investigating. We offer complimentary dementia-care consultations with a specialist who will tell you honestly what you're looking at. [LINK:/services/alzheimers-dementia]",
      },
      {
        h2: "8. The house looks \"off\"",
        body:
          "Trust your gut. If something feels different the moment you walk in — dust that wasn't there before, laundry piles, a lingering smell — it usually is. Light housekeeping restores the home's rhythm quickly.",
      },
      {
        h2: "9. Your parent is afraid to be alone at night",
        body:
          "This is often the quietest signal and the loudest cry for help. Overnight or 24-hour care turns dread into peace almost immediately.",
      },
      {
        h2: "10. You, the caregiving family member, are exhausted",
        body:
          "This counts. Spouse caregivers in particular tend to carry weight they shouldn't. Respite care gives you back a few hours, a full day, or a week — so you can keep showing up long-term.",
      },
      {
        h2: "How to start the conversation",
        body:
          "Lead with love, not logistics. \"Dad, I want to make sure your days feel easy — can we try someone two mornings a week, just to see?\" Starting small (companion care, a few hours) almost always leads to a smoother acceptance than announcing a full plan.",
      },
    ],
    faqs: [
      {
        q: "How do I know it's time to bring in a professional caregiver?",
        a: "If you've noticed two or more of the signals above, it's time to talk. A free consultation with our care coordinator will help you understand what level of care actually fits — often it's less than families fear.",
      },
      {
        q: "Can we start with just a few hours per week?",
        a: "Absolutely. Most of our Naples, Fort Myers, and Bonita Springs clients start with 2–3 companion visits per week and adjust as life changes.",
      },
      {
        q: "What if my parent refuses care?",
        a: "This is extremely common. We train our caregivers to arrive as a 'helpful friend' first. The first visit is usually a conversation, not a care plan. Trust comes before hours.",
      },
    ],
    related: ["private-pay-vs-medicaid-florida", "alzheimers-at-home-naples"],
  },
  {
    slug: "private-pay-vs-medicaid-florida",
    title: "Private Pay vs. Medicaid for Home Care in Florida: What SWFL Families Actually Need to Know",
    excerpt:
      "The honest, un-sugarcoated breakdown of how in-home care is paid for in Florida — and why private-pay families consistently receive better care.",
    date: "2026-02-21",
    readMinutes: 9,
    heroImage:
      "https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&w=1600&q=80",
    heroAlt:
      "Elderly couple reviewing financial documents at their kitchen table in Fort Myers",
    keywords:
      "private pay home care Florida, Medicaid home care SWFL, cost of in-home care Naples",
    sections: [
      {
        h2: "The three ways in-home senior care gets paid for",
        body:
          "Across Southwest Florida there are really only three funding paths: private pay (cash, retirement income, long-term-care insurance), VA Aid & Attendance for qualified veterans, and Florida Medicaid Long-Term Care (SMMC-LTC). Everything else — reverse mortgages, HELOCs, bridge loans — ultimately plugs into one of those three.",
      },
      {
        h2: "What private-pay actually costs in SWFL",
        body:
          "In Naples, Bonita Springs, and Estero in 2026, premium in-home care runs $30–$45 per hour depending on the acuity level and whether the shift is daytime, overnight, or 24-hour live-in. A typical starter plan of three 4-hour companion visits per week runs $1,500–$1,900/month. Full 24-hour live-in ranges $15,000–$22,000/month. [LINK:/home-care/naples]",
      },
      {
        h2: "Where private-pay quietly wins",
        body:
          "Private-pay clients receive consistent, matched caregivers (the same warm face every week) and almost always get faster response times, more detailed care notes, and a dedicated care coordinator. In short, you receive what you're paying for — and the gap between private-pay and reimbursed care in Florida is wider than most families expect.",
      },
      {
        h2: "Florida Medicaid Long-Term Care: eligibility snapshot",
        body:
          "Medicaid SMMC-LTC in Florida has strict income and asset limits ($2,901/month income cap in 2026, $2,000 countable assets for a single applicant). A 5-year lookback applies to any asset transfers. If a parent needs care but has recently gifted assets to children, Medicaid is almost certainly not an immediate option.",
      },
      {
        h2: "VA Aid & Attendance for Florida veterans",
        body:
          "If your parent is a wartime-era veteran (or the surviving spouse of one), VA Aid & Attendance can contribute $1,500–$3,000+/month toward home care costs. The paperwork is real, but it is one of the most under-used benefits in SWFL. We'll point you to a free, accredited VA claims agent — never charge for help with the application.",
      },
      {
        h2: "Long-term care insurance",
        body:
          "If your parent purchased an LTC policy in the 1990s–2000s, read it carefully. Many policies cover in-home care at a per-day rate (often $150–$300/day) once activities-of-daily-living triggers are met. We handle the care-plan documentation and timesheets most insurers require, which spares families a huge paperwork burden.",
      },
      {
        h2: "The honest answer: why most of our SWFL families go private-pay",
        body:
          "In 50-mile radius around Naples, the vast majority of families we work with go private-pay. The combination of (1) consistent caregivers, (2) concierge coordination, (3) no Medicaid-lookback stress, and (4) ability to customize services week-to-week is simply unmatched. For families with the means, it is the right investment in a parent's final decade.",
      },
    ],
    faqs: [
      {
        q: "Is Elite Home Care by Hortense a Medicaid provider?",
        a: "We serve private-pay, long-term-care-insurance, and VA Aid & Attendance clients. We are not an SMMC-LTC Medicaid provider — if that's your family's path, we're happy to refer you to a Medicaid-contracted agency and share the differences honestly.",
      },
      {
        q: "What is the average monthly cost of in-home care in Naples?",
        a: "For a typical starter plan (companion + meal prep, 10–15 hours/week), expect $1,500–$2,500/month. For 24-hour live-in care, $15,000–$22,000/month. We'll quote your exact plan during a free consultation.",
      },
      {
        q: "Does Medicare cover in-home care?",
        a: "Traditional Medicare covers short-term skilled home-health visits (physical therapy, wound care) after a hospital stay — not ongoing custodial care like bathing, meals, or companionship. Those are private-pay, LTC insurance, VA, or Medicaid.",
      },
    ],
    related: ["signs-elderly-parent-needs-in-home-care", "hurricane-prep-seniors-swfl"],
  },
  {
    slug: "alzheimers-at-home-naples",
    title: "Caring for a Parent with Alzheimer's at Home in Naples: A Family's Practical Guide",
    excerpt:
      "A realistic, kind, specific guide — written for SWFL families — on how to keep a parent with Alzheimer's safely, lovingly at home.",
    date: "2026-03-02",
    readMinutes: 10,
    heroImage:
      "https://images.unsplash.com/photo-1517867065866-b3eef5f0dfaa?auto=format&fit=crop&w=1600&q=80",
    heroAlt:
      "Caregiver gently assisting an elderly man with Alzheimer's at his home in Naples",
    keywords:
      "Alzheimer's home care Naples, dementia care SWFL, memory care at home",
    sections: [
      {
        h2: "First, breathe",
        body:
          "Alzheimer's is one of the longest, most unpredictable journeys a family will ever take. The first thing we tell every family is this: it does not have to be done perfectly, and it does not have to be done alone. [LINK:/services/alzheimers-dementia]",
      },
      {
        h2: "Stage-by-stage: what to expect at home",
        body:
          "Early stage focuses on routine and safety — labeled drawers, visual reminders, predictable meals. Middle stage adds hands-on help with dressing, bathing, and bathroom routines, and often requires a locked front door. Late stage shifts to full physical care, and often full-time or 24-hour support. Most Naples families move through these stages over 4 to 10 years.",
      },
      {
        h2: "The daily routine is the medicine",
        body:
          "People with Alzheimer's experience the day as texture, not time. A predictable rhythm — same breakfast table, same morning music, same afternoon walk around the courtyard — reduces anxiety more effectively than any medication. Our caregivers co-build these routines with the family within the first week.",
      },
      {
        h2: "Managing sundowning",
        body:
          "That restless, anxious, agitated hour in the late afternoon is the hardest. Soft light before dusk, a small warm meal, familiar music, and a trained caregiver gently redirecting conversation can shorten sundowning dramatically. If the family caregiver is a spouse, this is also the shift most worth outsourcing — it protects your evenings together.",
      },
      {
        h2: "Hurricane and heat preparedness",
        body:
          "SWFL summers are brutal, and hurricane season adds a layer most families don't think about until it's close. Someone with Alzheimer's cannot be put through an unfamiliar hotel room during an evacuation without consequence. We build a dementia-aware emergency plan with every memory-care client — including power contingencies, prescription cache, and a pre-identified family member's home.",
      },
      {
        h2: "When 24-hour care becomes necessary",
        body:
          "At some point most families cross a line where overnight wandering, falls, or anxiety require a caregiver on-site around the clock. Live-in or 24-hour rotating care is more affordable than most families expect in Florida and often cheaper than high-end memory-care facilities when a spouse is still at home. [LINK:/home-care/naples]",
      },
      {
        h2: "Taking care of the caregiver",
        body:
          "If you are the family caregiver — especially if you are the spouse — your well-being is the single biggest determinant of how long home care remains possible. Weekly respite care is not a luxury; it is the thing that keeps the whole plan viable.",
      },
    ],
    faqs: [
      {
        q: "Can someone with Alzheimer's really stay at home?",
        a: "In most cases, yes — through all stages — with the right support plan. The familiarity of home is itself therapeutic.",
      },
      {
        q: "How do your caregivers handle a client who refuses care?",
        a: "Our memory-care caregivers are trained in de-escalation and redirection. We don't argue, we don't correct, we join the client's reality. Trust always comes before tasks.",
      },
      {
        q: "Do you offer overnight dementia care in Naples?",
        a: "Yes. Overnight, live-in, and 24-hour rotating memory care are among our most-requested arrangements in Naples and Bonita Springs.",
      },
    ],
    related: ["signs-elderly-parent-needs-in-home-care", "hurricane-prep-seniors-swfl"],
  },
  {
    slug: "hurricane-prep-seniors-swfl",
    title: "Hurricane Prep for Seniors in SWFL: What Every Family Needs on the Shelf",
    excerpt:
      "A room-by-room, prescription-by-prescription checklist to keep an elderly parent in Naples or Fort Myers safe through hurricane season.",
    date: "2026-03-09",
    readMinutes: 6,
    heroImage:
      "https://images.unsplash.com/photo-1527751171053-6ba06b85a845?auto=format&fit=crop&w=1600&q=80",
    heroAlt: "Hurricane-ready supplies on a kitchen counter in a Fort Myers home",
    keywords: "hurricane prep seniors Naples, elderly hurricane kit SWFL",
    sections: [
      {
        h2: "The special risk hurricanes pose to seniors",
        body:
          "A 36-hour power outage is uncomfortable for a 40-year-old and genuinely dangerous for an 82-year-old on oxygen, insulin, or heart medication. Hurricane prep for an elderly SWFL parent is not optional — it's the single most important household audit of the year.",
      },
      {
        h2: "Medications: the 14-day rule",
        body:
          "Keep a minimum 14-day supply of every daily prescription in a labeled, waterproof bag. Review refill dates every June 1 and November 1. If any medication requires refrigeration, know your plan for a 72-hour outage before the storm, not during.",
      },
      {
        h2: "Power: what actually matters",
        body:
          "A full-house generator is ideal. Lacking that, a portable battery capable of running a CPAP, oxygen concentrator, or medical refrigerator for 24+ hours is a smart Christmas gift from an adult child. Have fuel for at least 48 hours — and make sure your parent can start the unit.",
      },
      {
        h2: "The evacuation question",
        body:
          "A parent with mobility issues or dementia should rarely be put through a shelter. Pre-arrange where they will go: a child's home further inland, a trusted neighbor outside the surge zone, or an evacuation plan with the fire department (sign up on Lee or Collier County's special-needs registry before July 1 each year).",
      },
      {
        h2: "Caregiver coverage during a storm",
        body:
          "A reliable in-home caregiver during a storm is a game-changer. Our caregivers who are rostered as 'hurricane staff' pre-stage with their client 48 hours before landfall and stay through the all-clear. That is often the single reason a parent weathers the storm safely in their own home. [LINK:/home-care/fort-myers]",
      },
      {
        h2: "After the storm: the quiet dangers",
        body:
          "Mold, mosquito-borne illness, and heat exhaustion in a powerless house kill more seniors in SWFL than the storm itself. Daily caregiver visits in the week after landfall are non-negotiable for frail elderly parents.",
      },
    ],
    faqs: [
      {
        q: "Do your caregivers work during hurricanes?",
        a: "Yes — our hurricane roster pre-stages 48 hours before landfall and stays through all-clear. We also help families coordinate evacuations when needed.",
      },
      {
        q: "What if my parent lives alone in Cape Coral and I'm out of state?",
        a: "This is one of our most common scenarios. We build a storm-specific care plan during the free consultation, including evacuation coordination and post-storm welfare checks.",
      },
    ],
    related: ["signs-elderly-parent-needs-in-home-care", "alzheimers-at-home-naples"],
  },
];

export function getPost(slug) {
  return BLOG_POSTS.find((p) => p.slug === slug);
}

export function getRelatedPosts(slug, limit = 3) {
  const post = getPost(slug);
  if (!post) return BLOG_POSTS.slice(0, limit);
  const related = (post.related || [])
    .map(getPost)
    .filter(Boolean);
  const fallback = BLOG_POSTS.filter((p) => p.slug !== slug && !related.find((r) => r.slug === p.slug));
  return [...related, ...fallback].slice(0, limit);
}
